import { useMemo } from "react";

import {
  type FieldDef,
  type ResourceConfig,
  ResourcePage,
  fmtDate,
} from "@/components/resource-page";
import { PROVINCE_FIELDS } from "@/pages/ProvincesPage";
import type { City } from "@/utils/geo";
import type { TranslationKey } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";

/**
 * What a city is made of. Exported because the club form adds one without
 * leaving itself, and a second copy of these six fields is a second place for
 * the API's required set to drift away from.
 */
export const CITY_FIELDS = (t: (key: TranslationKey) => string): FieldDef[] => [
  { name: "name", label: t("geo.name"), required: true },
  { name: "code", label: t("geo.code"), required: true },
  { name: "population", label: t("geo.population"), type: "number", min: 0, required: true },
  {
    name: "province_id",
    label: t("geo.province"),
    type: "fk",
    required: true,
    endpoint: "/provinces/compact",
    // The province a city belongs to is sometimes the one nobody has added
    // yet; sending someone to Provinces and back loses the half-filled form
    // on the way, as it did for a club and its city.
    create: {
      singular: t("geo.province"),
      endpoint: "/provinces",
      fields: PROVINCE_FIELDS(t),
      permission: "add__province",
    },
  },
  {
    name: "longitude",
    required: true,
    label: t("geo.longitude"),
    type: "number",
    step: "any",
    min: -180,
    max: 180,
  },
  {
    name: "latitude",
    required: true,
    label: t("geo.latitude"),
    type: "number",
    step: "any",
    min: -90,
    max: 90,
  },
];

export const citiesConfig = (t: (key: TranslationKey) => string): ResourceConfig<City> => ({
  title: t("geo.cities"),
  singular: t("geo.city"),
  plural: t("geo.cities"),
  endpoint: "/cities",
  model: "city",
  // The two halves of the same door: the list goes out as a CSV of the
  // columns on screen, and a CSV of those columns comes back in. The province
  // is named, not numbered — a spreadsheet holds names.
  exportable: true,
  // Written by the server, from the columns ticked in the dialog: the file
  // is every matching city, and the browser only ever holds a page of them.
  exportServer: { permission: "view__city" },
  importable: {
    columns: ["Name", "Code", "Province", "Population", "Latitude", "Longitude"],
    template: "/templates/cities-import-sample.csv",
    file: "cities-import-sample.csv",
    permission: "add__city",
  },
  // Deleting a place is never what anyone means: the members, clubs and
  // listings recorded against it still name it. Delete puts the row in the
  // archive, and the archive gives it back.
  archiveFilter: { name: "archived", value: "true", permission: "delete__city" },
  // Tick the rows, then one button above them: Delete on the list,
  // Restore in the archive. Behind the same right as deleting one.
  bulk: { permission: "delete__city" },
  restore: { permission: "delete__city" },
  confirmDeleteMessage: t("geo.confirmArchive"),
  columns: [
    {
      key: "id",
      label: t("rp.id"),
      sortKey: "id",
      render: (row) => (
        <span className="text-accent underline underline-offset-4">{row.id}</span>
      ),
    },
    { key: "name", label: t("geo.name"), sortKey: "name", render: (row) => row.name },
    {
      key: "code",
      label: t("geo.code"),
      sortKey: "code",
      render: (row) => <span className="font-mono">{row.code}</span>,
    },
    { key: "province", label: t("geo.province"), render: (row) => row.province_name ?? "—" },
    {
      key: "population",
      label: t("geo.population"),
      sortKey: "population",
      render: (row) => row.population ?? "—",
    },
    { key: "latitude", label: t("geo.latitude"), render: (row) => row.latitude ?? "—" },
    { key: "longitude", label: t("geo.longitude"), render: (row) => row.longitude ?? "—" },
    {
      key: "created_by",
      label: t("geo.createdBy"),
      render: (row, lookup) => lookup("created_by_id", row.created_by_id),
    },
    {
      key: "created_at",
      label: t("geo.createdAt"),
      sortKey: "created_at",
      render: (row) => fmtDate(row.created_at),
    },
  ],
  defaultSort: { key: "id", order: "desc" },
  // The Employees row: a small search and a funnel on the left, the screen's
  // own actions as icons on the right.
  toolbarVariant: "compact",
  // Add, view and edit as sheets over the list, the way Employees does it:
  // these records are short, and the table behind is the context for them.
  formVariant: "modal",
  viewVariant: "modal",
  searchPlaceholder: t("rp.searchShort"),
  filters: [
    {
      name: "created_by_id",
      label: t("geo.createdByFilter"),
      type: "fk",
      endpoint: "/users/compact",
    },
    { name: "created_from", label: t("geo.createdFrom"), type: "date" },
    { name: "created_to", label: t("geo.createdTo"), type: "date" },
    {
      name: "province_id",
      label: t("geo.province"),
      type: "fk",
      endpoint: "/provinces/compact",
    },
  ],
  fields: CITY_FIELDS(t),
  detailLabels: {
    population: t("geo.population"),
    latitude: t("geo.latitude"),
    longitude: t("geo.longitude"),
    code: t("geo.code"),
    province_name: t("geo.province"),
  },
  toForm: (row) => ({
    name: row.name,
    code: row.code,
    province_id: String(row.province_id),
    population: row.population?.toString() ?? "",
    latitude: row.latitude?.toString() ?? "",
    longitude: row.longitude?.toString() ?? "",
  }),
});

function CitiesPage() {
  const t = useTranslation();
  const config = useMemo(() => citiesConfig(t), [t]);
  return <ResourcePage config={config} />;
}

export default CitiesPage;
